import {
    ColumnsMatching,
    PartialSupportResultsStatusFilters,
    RequestResultsStatus,
    SupportRequestResultsModalTableRow,
    SupportRequestResultsResponse,
} from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import { format } from 'date-fns';
import { buildCSV, createCsvData } from 'utils/create-csv/create-csv';
import isNil from 'lodash/isNil';
import { store } from 'store/store';
import { selectAuthUserAccount } from 'store/selectors/app-selectors';
import { DEFAULT_FT_RADIUS } from 'features/insights/consts/consts';
import type { Filter } from '@placer-ui/types';
import { generateInsightPagePath } from 'router/go-to-routes';
import pluralize from 'pluralize';

export const ORIGINAL_DATA_PREFIX = 'src_';
export const ORIGINAL_CHALLENGE_PREFIX = 'challenge_';

export const sumItemsByStatus = (data: SupportRequestResultsModalTableRow[]) => {
    const statusMap = data.reduce((acc, { status, isEdited }) => {
        if (acc[status as RequestResultsStatus]) {
            acc[status as RequestResultsStatus]++;
        } else {
            acc[status as RequestResultsStatus] = 1;
        }

        if (isEdited) {
            acc['Edited'] = (acc['Edited'] ?? 0) + 1;
        }

        return acc;
    }, {} as Record<RequestResultsStatus, number>);

    const statusList = Object.keys(statusMap ?? {}).map((status) => ({
        label: status as RequestResultsStatus,
        value: statusMap?.[status as RequestResultsStatus] ?? 0,
    }));

    return { statusMap, statusList };
};

export const getFilterDataBySelectedStatuses = (
    filters: PartialSupportResultsStatusFilters,
    status: RequestResultsStatus,
    isEdited: boolean = false,
) => {
    if (Object.values(filters).every((value) => !value)) {
        return true;
    }

    return (isEdited && !!filters['Edited']) || !!filters[status];
};

export const generateCsvFileName = ({
    name,
    withTimeFormat = false,
    withAccountName = true,
    fileType = 'csv',
}: {
    name: string;
    withTimeFormat?: boolean;
    withAccountName?: boolean;
    fileType?: 'csv' | 'xls';
}) => {
    const { display_name = '' } = withAccountName
        ? selectAuthUserAccount(store.getState()) ?? {}
        : {};
    const date = format(new Date(), `yyyyMMdd${withTimeFormat ? '_hh:mmaa' : ''}`);

    return `${display_name ? `${display_name}_` : ''}${name}_${date}.${fileType}`;
};

export const mapSupportTableDataToCsvRowDataByHeaderKeys = (
    data: SupportRequestResultsModalTableRow[],
    headers: string[],
) => {
    return data.map((item) => {
        return headers.map((key) => {
            return item[
                key as keyof Omit<
                    SupportRequestResultsModalTableRow,
                    'actions' | 'isLoading' | 'isEdited' | 'flag_reasons'
                >
            ]!;
        });
    });
};

export const getBaseHeaderKeys = ({
    columnsMatching,
    avoidAddressTypeCheck = false,
    excludeId = false,
}: {
    columnsMatching: ColumnsMatching | undefined;
    avoidAddressTypeCheck?: boolean;
    excludeId?: boolean;
}) => {
    const addressType = getAddressTypeFromColumnMapping(columnsMatching);

    return [
        'name',
        ...(addressType === 'separate_columns' || avoidAddressTypeCheck
            ? ['street', 'city', 'state']
            : []),
        ...(addressType === 'formatted_address' || avoidAddressTypeCheck
            ? ['formatted_address']
            : []),
        ...(addressType === 'lat_lng' || avoidAddressTypeCheck ? ['lat', 'lng'] : []),
        'zip_code',
        ...(excludeId ? [] : ['id']),
    ];
};

// TODO - move to mapping utils
export const generateResolverDataToProcessingCsvFile = (
    data: SupportRequestResultsModalTableRow[],
    mappedFileData: ColumnsMatching | undefined,
) => {
    // TODO - structure of the original resolver - should be in the BE
    const baseHeaders = getBaseHeaderKeys({ columnsMatching: mappedFileData, excludeId: true });

    const rows = mapSupportTableDataToCsvRowDataByHeaderKeys(data, baseHeaders);

    const headers = baseHeaders.map((key) => {
        return (
            {
                street: 'address',
            }[key] ?? key
        );
    });

    const fileName = generateCsvFileName({ name: 'property_list', withTimeFormat: true });
    const csv = buildCSV(headers, rows);

    return [new File([createCsvData(csv)], fileName)];
};

export const getAddressTypeFromColumnMapping = (mappedFileData: ColumnsMatching | undefined) => {
    if (!mappedFileData) {
        return '';
    }

    if (!isNil(mappedFileData['formatted_address'])) {
        return 'formatted_address';
    } else if (!isNil(mappedFileData['lat']) && !isNil(mappedFileData['lng'])) {
        return 'lat_lng';
    } else if (
        !isNil(mappedFileData['city']) &&
        !isNil(mappedFileData['state']) &&
        !isNil(mappedFileData['street'])
    ) {
        return 'separate_columns';
    }
};

export const getEntityReportLink = (item: SupportRequestResultsResponse) => {
    if (!item.entity_id || !item.entity_type || item.status !== 'Available') return '';

    const filters =
        item.report_type === 'Nearby'
            ? [
                  {
                      config: {
                          ft_radius: DEFAULT_FT_RADIUS,
                      },
                  } as Filter,
              ]
            : undefined;

    const { fullAbsolutePath } = generateInsightPagePath({
        id: item.entity_id,
        filters,
        collection: pluralize(item.entity_type),
    });

    return fullAbsolutePath;
};
